package com.crowpay.views.theming

import com.crowpay.views.components.nav.AppDimensions
import com.crowpay.Resources
import com.crowpay.views.components.nav.CrowPayDimensions
import com.lightningkite.kiteui.ViewWrapper
import com.lightningkite.kiteui.models.*
import com.lightningkite.kiteui.reactive.lens
import com.lightningkite.kiteui.views.ViewModifierDsl3
import com.lightningkite.kiteui.views.ViewWriter
import com.lightningkite.kiteui.views.themeFromLast
import kotlin.collections.mapOf
import kotlin.time.Duration.Companion.milliseconds

data object Outlined : Semantic {
    override val key: String = "outlnd"
    override fun default(theme: Theme): ThemeAndBack = theme.copy(
        id = key,
        cornerRadii = CornerRadii.Constant(AppDimensions.cornerRadii),
        outlineWidth = AppDimensions.outlineWidth,
    ).withBack
}

@ViewModifierDsl3
val ViewWriter.outlined: ViewWrapper get() = Outlined.onNext

@ViewModifierDsl3
val invalidOutlineTheme: Theme.() -> Theme = {
    copy(
        cornerRadii = CornerRadii.Constant(AppDimensions.cornerRadii),
        outlineWidth = AppDimensions.outlineWidth,
        outline = AppColors.red.main
    )
}

@ViewModifierDsl3
val ViewWriter.invalidOutlined: ViewWrapper get() = themeFromLast(invalidOutlineTheme)

@ViewModifierDsl3
val ViewWriter.redText: ViewWrapper get() = ForegroundSemantic(AppColors.red.main).onNext

object AuthDialogSemantic: Semantic {
    override val key: String = "athdlg"
    override fun default(theme: Theme): ThemeAndBack =
        theme.copy(
            id = "athdlg",
            cornerRadii = CornerRadii.Constant(1.rem),
            background = Color.white,
            elevation = 1.dp,
            revert = true,
            derivations = mapOf(
                CardSemantic to { it.withoutBack }
            )
        )
            .withBack
}

object RemoveBackgroundSemantic : Semantic {
    override val key: String = "noBck"
    override fun default(theme: Theme): ThemeAndBack = theme.copy(
        id = "noBck",
        background = Color.transparent
    ).withoutBack
}

object HiddenButtonSemantic : Semantic {
    override val key: String = "hdnBtn"
    override fun default(theme: Theme): ThemeAndBack = theme.copy(
        id = "hdnBtn",
        derivations = mapOf(
            ButtonSemantic to {
                it.withBack
            },
            HoverSemantic to {
                it.withBack
            },
            DownSemantic to {
                it.withBack
            },
        )
    ).withoutBack
}

data object OverlayHover : Semantic {
    override val key: String = "ovrlyhvr"
    override fun default(theme: Theme): ThemeAndBack = theme.copy(
        id = key,
        background = Color.transparent,
        derivations = mapOf(
            HoverSemantic to {
                it.copy(
                    id = "hvr",
                    background = Color.black.applyAlpha(0.1f)
                ).withBackNoPadding
            }
        )
    ).withBackNoPadding
}

object Unread : Semantic {
    override val key: String = "unrd"
    override fun default(theme: Theme): ThemeAndBack = theme.copy(
        id = key,
        cornerRadii = CornerRadii.ForceConstant(0.5.rem),
        font = theme.font.copy(size = theme.font.size * 2 / 3, bold = true, lineSpacingMultiplier = 1.0),
        spacing = 0.1.rem,
        iconOverride = AppColors.secondary.main,
        derivations = mapOf(
            CardSemantic to {
                it.copy(
                    id = "crd",
                    background = AppColors.secondary.main,
                    foreground = Color.white
                ).withBack
            }
        )
    ).withoutBack
}

object InitialIconSemantic : Semantic {
    override val key: String = "ntIcn"
    override fun default(theme: Theme): ThemeAndBack {
        val backIsWhite = theme.background.closestColor() == Color.white
        return theme[ButtonSemantic].theme.copy(
            cornerRadii = CornerRadii.RatioOfSize(1f),
            background = if (backIsWhite) AppColors.primary.main else theme.background,
            foreground = if (backIsWhite) AppColors.primary.light3 else Color.white,
            outline = if (backIsWhite) theme.outline else Color.white,
            outlineWidth = if (backIsWhite) 0.dp else 1.dp,
            spacing = 3.dp,
            font = theme.font.copy(
                lineSpacingMultiplier = 1.0,
                weight = 800,
                size = 1.rem,
                allCaps = true
            )
        ).withBack
    }
}

object MessageSectionSubHeaderSemantic : Semantic {
    override val key: String = "msgSbhdr"
    override fun default(theme: Theme): ThemeAndBack = theme.copy(
        font = theme.font.copy(allCaps = true),
        foreground = theme.foreground.applyAlpha(0.3f)
    ).withoutBack
}

@ViewModifierDsl3
val ViewWriter.hiddenButton: ViewWrapper get() = HiddenButtonSemantic.onNext

data object PunchFieldSemantic: Semantic {
    override val key: String = "pnchfld"
    override fun default(theme: Theme): ThemeAndBack = theme[FieldSemantic].theme.copy(
        id = key,
        background = AppColors.grey.light2
    ).withBack
}

data object RemoveOutline: Semantic {
    override val key: String get() = "rmvout"
    override fun default(theme: Theme): ThemeAndBack = theme.copy(outlineWidth = 0.px).withoutBack
}

data object MobileNavSemantic : Semantic {
    override val key: String = "mobnav"
    override fun default(theme: Theme): ThemeAndBack = theme.copy(
        id = key,
        derivations = mapOf(
            BarSemantic to { bar ->
                bar.copy(
                    id = "bar",
                    background = AppColors.primary.dark,
                    foreground = Color.white,
                    derivations = mapOf(
                        HoverSemantic to {
                            it.copy(id = "hvr", background = AppColors.purple.main).withBack
                        },
                    )
                ).withBack
            },
            NavSemantic to { nav ->
                nav.copy(
                    id = "nav",
                    derivations = mapOf(
                        SelectedSemantic to {
                            it.copy(
                                id = "sel",
                                foreground = AppColors.secondary.main
                            ).withoutBack
                        },
                        UnselectedSemantic to {
                            it.copy(
                                id = "unsel",
                                background = Color.white,
                                foreground = AppColors.primary.main,
                            ).withoutBack
                        },
                    )
                ).withBack
            },
        )
    ).withoutBack
}

@ViewModifierDsl3 val ViewWriter.removeOutline: ViewWrapper get() = RemoveOutline.onNext

data class SetCornerRadii(val revert: Boolean = false, val calculation: (Theme)->CornerRadii?) : Semantic {
    override val key: String = "crnr"
    override fun default(theme: Theme): ThemeAndBack =
        calculation(theme)
            ?.let { cor ->
                theme.copy(
                    id = key + cor.toString().filter { it.isLetterOrDigit() },
                    cornerRadii = cor,
                    revert = revert,
                ).withoutBack
            }
            ?: theme.withoutBack
}

data object MaxRadiiSemantic: Semantic {
    override val key: String = "rndBck"
    override fun default(theme: Theme): ThemeAndBack = theme.copy(cornerRadii = CornerRadii.RatioOfSize(0.5f)).withBack
}

data object ListSemantic : Semantic {
    override val key: String = "list"
    override fun default(theme: Theme): ThemeAndBack = theme.copy(
        id = key,
        spacing = 0.5.rem,
        revert = true
    ).withoutBack
}

fun ThemeDerivation.withoutBack() = object : ThemeDerivation {
    override fun invoke(theme: Theme): ThemeAndBack = this@withoutBack.invoke(theme).theme.withoutBack
}

fun Paint.matchAlpha(other: Paint) = other.closestColor().alpha.let { alpha ->
    map { it.copy(alpha = alpha) }
}

val appThemeLive = CrowPayDimensions.live.lens { dimensions ->
    Theme(
        id = "main",
        font = FontAndStyle(font = Resources.fontsRobotoRegular),
        transitionDuration = 100.milliseconds,
        foreground = Color.black,
        background = Color.white,
        elevation = 0.dp,
        navSpacing = 0.dp,
        cornerRadii = CornerRadii.Constant(0.px),
        outlineWidth = 0.dp,
        outline = AppColors.grey.light1,
        derivations = mapOf(
            ErrorSemantic to {
                it.copy(
                    foreground = AppColors.red.main
                ).withoutBack
            },
            ButtonSemantic to {
                it
                    .copy(
                        id = "btn",
                        cornerRadii = CornerRadii.Constant(dimensions.cornerRadii),
                        spacing = dimensions.buttonSpacing,
                    )
                    .withBack
            },
            HoverSemantic to {
                it
                    .copy(background = it.background.darken(0.2f))
                    .withBack
            },
            DownSemantic to {
                it
                    .copy(background = it.background.darken(0.4f))
                    .withBack
            },
            CardSemantic to {
                val currentIsDark = it.background.closestColor().toHSP().brightness < 0.95
                it[Outlined].theme
                    .copy(
                        id = "crd",
                        background = if (currentIsDark) Color.white else AppColors.grey.light2
                    )
                    .withBack
            },
            FieldSemantic to {
                it[Outlined]
            },
            FocusSemantic to {
                it.copy(
                    id = "fcs",
                    outline = if (it.outline.closestColor() == AppColors.red.light2) AppColors.red.light1 else AppColors.grey.main
                ).withBack
            },
            DialogSemantic to {
                it.copy(
                    id = "dlg",
                    cornerRadii = CornerRadii.Constant(dimensions.cornerRadii),
                    outlineWidth = dimensions.outlineWidth,
                    derivations = mapOf(
                        CardSemantic to { it.withoutBack }
                    )
                )
                    .withBack
            },
            NavSemantic to {
                it.copy(
                    id = "nav",
                    background = AppColors.primary.dark,
                    foreground = Color.white,
                    derivations = mapOf(
                        HoverSemantic to {
                            it.copy(id = "hvr", background = AppColors.purple.main).withBack
                        },
                        DownSemantic to {
                            it.copy(id = "dwn", background = AppColors.purple.dark).withBack
                        },
                        SelectedSemantic to {
                            it.copy(id = "sel", background = AppColors.purple.main).withBack
                        },
                        UnselectedSemantic to {
                            it.copy(id = "usel", background = AppColors.primary.dark).withBack
                        }
                    ),
                ).withBack
            },
            BarSemantic to {
                it.copy(
                    id = "bar",
                    iconOverride = AppColors.primary.main,
                ).withBack
            },
            InvalidSemantic to {
                it.copy(
                    id = "inv",
                    outline = AppColors.red.main,
                    outlineWidth = dimensions.outlineWidth
                ).withBack
            },
            SelectedSemantic to {
                it
                    .copy(
                        id = "sel",
                        background = AppColors.grey.light2,
                        outlineWidth = 0.px,
                        outline = Color.transparent
                    )
                    .withBack
            },
            UnselectedSemantic to {
                it
                    .copy(
                        id = "unsel",
                        background = Color.white,
                        outlineWidth = dimensions.outlineWidth,
                        outline = AppColors.grey.main.matchAlpha(it.foreground)
                    ).withBack
            },
            WarningSemantic to {
                it.copy(
                    id = "wrn",
                    background = AppColors.yellow.main,
                ).withBack
            },
            DangerSemantic to {
                it.copy(
                    id = "dngr",
                    background = AppColors.red.light1
                ).withBack
            },
        ),
    )
}

val appTheme get() = appThemeLive.value